<template>
  <div class="client-modern">
    <!-- Modals -->
    <b-modal
      size="lg"
      no-close-on-backdrop
      hide-footer
      id="resident"
      title="ADICIONAR RESIDENTE COMO CLIENTE"
    >
      <Resident @success="paginate" :ufs="ufs" :banks="banks" />
    </b-modal>

    <b-modal
      size="xl"
      no-close-on-backdrop
      hide-footer
      id="create"
      title="NOVO CLIENTE"
    >
      <Create
        @success="handleSuccessCreate"
        :residents="residentsActivesByDontLinkedWithFinanceClient"
        :ufs="ufs"
        :banks="banks"
      />
    </b-modal>

    <b-modal
      size="lg"
      no-close-on-backdrop
      hide-footer
      id="import"
      title="IMPORTAR CLIENTE(S)"
    >
      <Import @success="paginate" />
    </b-modal>

    <div class="client-container">
      <!-- Header Section -->
      <div class="client-header">
        <div class="header-content">
          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-users title-icon"></i>
              Clientes
            </h1>
            <p class="page-subtitle">
              Gerencie clientes e residentes vinculados ao sistema financeiro
            </p>
          </div>

          <div class="header-actions">
            <v-btn
              v-if="$route.query.migrate"
              :disabled="loading"
              @click="createResidents"
              class="btn-migrate"
            >
              <i class="fas fa-download mr-2"></i>
              {{ loading ? "CARREGANDO..." : "IMPORTAR RESIDENTES" }}
            </v-btn>

            <v-btn
              v-if="permission('write', 'financial_client')"
              @click="$bvModal.show('import')"
              class="btn-import"
            >
              <i class="fas fa-file-upload mr-2"></i>
              IMPORTAR
            </v-btn>

            <v-btn
              v-if="permission('write', 'financial_client')"
              @click="$bvModal.show('create')"
              class="btn-add-modern"
            >
              <i class="fas fa-plus mr-2"></i>
              ADICIONAR CLIENTE
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="client-content">
        <div class="content-card">
          <!-- Search Section -->
          <div class="search-section">
            <div class="search-container">
              <label class="search-label">
                <i class="fas fa-search search-icon"></i>
                Pesquisar Clientes
              </label>
              <input
                type="text"
                placeholder="Digite o nome, CPF/CNPJ ou telefone..."
                class="search-input"
                v-model="search"
                @input="handleInputSearch"
              />
            </div>
          </div>

          <!-- Table Section -->
          <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
            <div class="table-container">
              <v-data-table
                v-show="data.length > 0"
                dense
                hide-default-footer
                disable-pagination
                :headers="headers"
                :items="data"
                :search="search"
                class="modern-table"
                :footer-props="{
                  'items-per-page-options': [50, 75, 100],
                  'items-per-page-text': 'Mostrar',
                }"
              >
                <!-- Nome Column -->
                <template #[`item.name`]="{ item }">
                  <div class="client-info">
                    <div class="client-avatar">
                      <i class="fas fa-user"></i>
                    </div>
                    <div class="client-details">
                      <v-tooltip bottom max-width="500">
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            v-bind="attrs"
                            v-on="on"
                            class="client-name-container"
                          >
                            <p class="client-name">{{ item.name }}</p>
                            <p v-if="item.resident_name" class="resident-name">
                              <i class="fas fa-home mr-1"></i>
                              Residente: {{ item.resident_name }}
                            </p>
                          </div>
                        </template>
                        <div class="tooltip-content">
                          <div class="tooltip-section">
                            <small class="tooltip-label">Nome</small>
                            <p class="tooltip-value">{{ item.name }}</p>
                          </div>
                          <div
                            v-if="item.resident_name"
                            class="tooltip-section"
                          >
                            <small class="tooltip-label">Residente</small>
                            <p class="tooltip-value">
                              {{ item.resident_name }}
                            </p>
                          </div>
                        </div>
                      </v-tooltip>
                    </div>
                  </div>
                </template>

                <!-- CPF/CNPJ Column -->
                <template #[`item.cpf_cnpj`]="{ item }">
                  <span class="document-number">{{ item.cpf_cnpj }}</span>
                </template>

                <!-- Telefone Column -->
                <template #[`item.telephone`]="{ item }">
                  <span v-if="item.telephone" class="phone-number">
                    <i class="fas fa-phone mr-1"></i>
                    {{ item.telephone }}
                  </span>
                  <span v-else class="no-data">-</span>
                </template>

                <!-- Banco Column -->
                <template #[`item.bank_name`]="{ item }">
                  <span v-if="item.bank_name" class="bank-info">
                    <i class="fas fa-university mr-1"></i>
                    {{ item.bank_name }}
                  </span>
                  <span v-else class="no-data">-</span>
                </template>

                <!-- Agência/Conta Column -->
                <template #[`item.account`]="{ item }">
                  <span v-if="item.account" class="account-info">{{
                    item.account
                  }}</span>
                  <span v-else class="no-data">-</span>
                </template>

                <!-- Status Column -->
                <template #[`item.status`]="{ item }">
                  <span
                    class="status-badge"
                    :class="
                      item.status == 1 ? 'status-active' : 'status-inactive'
                    "
                  >
                    <i
                      :class="
                        item.status == 1
                          ? 'fas fa-check-circle'
                          : 'fas fa-times-circle'
                      "
                      class="mr-1"
                    ></i>
                    {{ item.status == 1 ? "ATIVO" : "INATIVO" }}
                  </span>
                </template>

                <!-- Action Column -->
                <template #[`item.action`]="{ item }">
                  <b-modal
                    size="xl"
                    no-close-on-backdrop
                    hide-footer
                    :id="`edit-${item.id}`"
                    :title="`EDITAR CLIENTE: ${item.name}`"
                  >
                    <Edit
                      @success="handleSuccessEdit"
                      :residents="residentsActivesByDontLinkedWithFinanceClient"
                      :all_residents="residents"
                      :data="item"
                      :ufs="ufs"
                      :banks="banks"
                      :finance_client="finance_client"
                    />
                  </b-modal>

                  <v-btn
                    v-if="permission('edit', 'financial_client')"
                    small
                    @click="edit(item)"
                    class="btn-edit-modern"
                  >
                    <i class="fas fa-edit mr-1"></i>
                    EDITAR
                  </v-btn>
                </template>
              </v-data-table>

              <!-- No Results -->
              <div v-show="data.length == 0" class="no-results">
                <i class="fas fa-users no-results-icon"></i>
                <h3 class="no-results-title">Nenhum cliente encontrado</h3>
                <p class="no-results-text">
                  Não há clientes cadastrados ou que correspondam à sua pesquisa
                </p>
                <v-btn
                  v-if="permission('write', 'financial_client')"
                  @click="$bvModal.show('create')"
                  class="btn-add-first"
                >
                  <i class="fas fa-plus mr-2"></i>
                  ADICIONAR PRIMEIRO CLIENTE
                </v-btn>
              </div>
            </div>
          </b-overlay>

          <!-- Pagination -->
          <div class="pagination-section" v-if="data.length > 0">
            <div class="pagination-info">
              <span class="results-info">
                Mostrando {{ data.length }} de {{ total }} clientes
              </span>
            </div>
            <b-pagination
              v-model="page"
              :total-rows="total"
              :per-page="25"
              @input="paginate()"
              class="modern-pagination"
              align="center"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Resident from "./Client/Resident";
import Import from "./Client/Import";
import Create from "./Client/Create";
import Edit from "./Client/Edit";
import api from "../../services/api";
import { getStatic } from "../../lib/functions";
import toast from "../../lib/ToastNotification";
import permission from "../../lib/permission";

export default {
  name: "Client",
  components: {
    Resident,
    Import,
    Create,
    Edit,
  },
  data() {
    return {
      search: "",
      headers: [
        { text: "Cliente", value: "name", sortable: true },
        { text: "CPF/CNPJ", value: "cpf_cnpj", sortable: true },
        { text: "Telefone", value: "telephone", sortable: false },
        { text: "Banco", value: "bank_name", sortable: true },
        { text: "Agência/Conta", value: "account", sortable: false },
        { text: "Status", value: "status", sortable: true },
        { text: "Ações", value: "action", sortable: false },
      ],
      ufs: [],
      banks: [],
      data: [],
      finance_client: [],
      filtered: [],
      residents: [],
      residentsActivesByDontLinkedWithFinanceClient: [],
      page: 1,
      total: 0,
      loading: false,
    };
  },
  created() {
    this.retrieve();
  },
  methods: {
    permission,

    handleInputSearch() {
      this.page = 1;
      this.paginate();
    },

    handleSuccessEdit() {
      this.getResidentsActivesByDontLinkedWithFinanceClient();
      this.paginate();
    },

    handleSuccessCreate() {
      this.getResidentsActivesByDontLinkedWithFinanceClient();
      this.paginate();
    },

    filter(text) {
      let { data } = this;
      let filtered = data.filter(
        ({ name }) =>
          String(name).toLowerCase().indexOf(String(text).toLowerCase()) > -1
      );
      this.filtered = filtered;
    },

    async retrieve() {
      this.getResidents();
      this.getResidentsActivesByDontLinkedWithFinanceClient();
      this.paginate();
      this.financeClient();

      try {
        this.ufs = (await getStatic("/ufs", true)).ufs;
        this.banks = (await getStatic("/banks", true)).banks;
      } catch (errors) {
        this.errors = errors;
      }
    },

    async financeClient() {
      try {
        const response = await api.get("/rest-home/financial/client-actives");
        const { data } = response.data;
        this.finance_client = data;
      } catch (error) {
        console.log(error);
      }
    },

    async getResidents() {
      try {
        this.loading = true;
        const response = await api.get("/rest-home/resident/all");
        const { residents } = response.data;
        this.residents = residents;
        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },

    async getResidentsActivesByDontLinkedWithFinanceClient() {
      try {
        this.loading = true;
        const response = await api.get(
          "/rest-home/financial/residents-actives-by-dont-linked-with-financial-client"
        );
        const { data } = response.data;
        this.residentsActivesByDontLinkedWithFinanceClient = data;
        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },

    async paginate() {
      try {
        this.loading = true;
        const { page, search } = this;
        let params = { page, search };

        const response = await api.get("/rest-home/financial/client", {
          params,
        });

        const { data, total } = response.data;
        this.data = data;
        this.filtered = data;
        this.total = total;

        this.loading = false;
        this.$bvModal.hide("import");
      } catch (error) {
        console.log(error);
      }
    },

    async createResidents() {
      try {
        this.loading = true;
        const response = await api.post(
          "/rest-home/financial/client/import/residents"
        );
        const { error, errors, message } = response.data;

        if (!error) {
          this.paginate();
          toast(message);
          this.errors = [];
        } else {
          this.errors = errors;
        }
        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },

    edit(client) {
      this.$bvModal.show(`edit-${client.id}`);
    },
  },
};
</script>

<style scoped>
.client-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.client-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Section */
.client-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

/* Modern Buttons */
.btn-add-modern {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-add-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4) !important;
}

.btn-import {
  background: linear-gradient(135deg, #1f5a76 0%, #0f4c75 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 4px 15px rgba(31, 90, 118, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-import:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(31, 90, 118, 0.4) !important;
}

.btn-migrate {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-migrate:hover:not(:disabled) {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4) !important;
}

/* Content Card */
.content-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* Search Section */
.search-section {
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-bottom: 1px solid #e5e7eb;
}

.search-container {
  max-width: 500px;
}

.search-label {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.search-icon {
  color: #6b7280;
  margin-right: 8px;
}

.search-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
}

.search-input:focus {
  outline: none;
  border-color: #0f65b0;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1);
}

.search-input::placeholder {
  color: #9ca3af;
}

/* Table Section */
.table-container {
  padding: 24px;
}

.loading-overlay {
  border-radius: 0;
  min-height: 400px;
}

/* Modern Table Styles */
.modern-table >>> .v-data-table__wrapper {
  border-radius: 12px;
  overflow: hidden;
}

.modern-table >>> table {
  border-collapse: separate;
  border-spacing: 0;
}

.modern-table >>> thead tr {
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
}

.modern-table >>> thead th {
  background: transparent !important;
  color: white !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  font-size: 12px !important;
  padding: 16px 12px !important;
  border: none !important;
}

.modern-table >>> tbody tr {
  transition: all 0.3s ease;
}

.modern-table >>> tbody tr:hover {
  background: #f8fafc !important;
  transform: scale(1.01);
}

.modern-table >>> tbody td {
  padding: 16px 12px !important;
  border-bottom: 1px solid #f1f5f9 !important;
  vertical-align: middle !important;
}

/* Client Info Styles */
.client-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.client-avatar {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
}

.client-name-container {
  min-width: 0;
}

.client-name {
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 2px 0;
  font-size: 14px;
}

.resident-name {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  display: flex;
  align-items: center;
}

/* Table Cell Styles */
.document-number {
  font-family: "Courier New", monospace;
  font-weight: 500;
  color: #374151;
}

.phone-number {
  color: #374151;
  display: flex;
  align-items: center;
}

.bank-info {
  color: #374151;
  display: flex;
  align-items: center;
}

.account-info {
  font-family: "Courier New", monospace;
  font-weight: 500;
  color: #374151;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-active {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.status-inactive {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

/* Action Button */
.btn-edit-modern {
  background: linear-gradient(135deg, #1d75be 0%, #155a8a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  font-size: 11px !important;
  box-shadow: 0 2px 8px rgba(29, 117, 190, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-edit-modern:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(29, 117, 190, 0.4) !important;
}

/* No Results */
.no-results {
  text-align: center;
  padding: 64px 24px;
}

.no-results-icon {
  font-size: 64px;
  color: #d1d5db;
  margin-bottom: 20px;
}

.no-results-title {
  font-size: 24px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.no-results-text {
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 24px 0;
}

.btn-add-first {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3) !important;
}

/* Pagination Section */
.pagination-section {
  padding: 24px;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.results-info {
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
}

.modern-pagination >>> .page-item.active .page-link {
  background-color: #0f65b0 !important;
  border-color: #0f65b0 !important;
  color: white !important;
  border-radius: 8px !important;
}

.modern-pagination >>> .page-link {
  border-radius: 8px !important;
  margin: 0 2px !important;
  border: 2px solid #e5e7eb !important;
  color: #374151 !important;
  font-weight: 500 !important;
}

.modern-pagination >>> .page-link:hover {
  background-color: #f3f4f6 !important;
  border-color: #d1d5db !important;
}

/* Tooltip Content */
.tooltip-content {
  max-width: 300px;
}

.tooltip-section {
  margin-bottom: 8px;
}

.tooltip-section:last-child {
  margin-bottom: 0;
}

.tooltip-label {
  color: #6b7280;
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.tooltip-value {
  color: #1e293b;
  font-size: 13px;
  font-weight: 500;
  margin: 2px 0 0 0;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-content {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .title-section {
    text-align: center;
  }

  .header-actions {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .client-container {
    padding: 0 16px;
  }

  .page-title {
    font-size: 24px;
    justify-content: center;
  }

  .search-section {
    padding: 20px;
  }

  .table-container {
    padding: 16px;
    overflow-x: auto;
  }

  .pagination-section {
    flex-direction: column;
    gap: 12px;
    padding: 20px;
  }

  .header-actions {
    flex-direction: column;
    gap: 8px;
  }

  .client-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .client-avatar {
    width: 32px;
    height: 32px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 20px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .modern-table >>> tbody td {
    padding: 12px 8px !important;
  }

  .modern-table >>> thead th {
    padding: 12px 8px !important;
    font-size: 11px !important;
  }
}
</style>